import React from "react";
import { Card, Table, Tag, Typography, Space, Button, Tooltip, Progress, Empty } from "antd";
import {
  EyeOutlined,
  SelectOutlined,
  DollarOutlined,
  BarChartOutlined,
  EditOutlined,
  TrophyOutlined,
  FireOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { format } from "date-fns";

const { Text } = Typography;

const TopPromotionsTable = ({
  title = "Top Performing Promotions",
  data = [],
  loading = false,
  onViewAnalytics,
  onEdit,
  showRank = true,
  limit = 10,
  style = {},
}) => {
  // Get type color
  const getTypeColor = (type) => {
    const colors = {
      banner: "blue",
      popup: "purple",
      floating: "cyan",
      inline: "green",
      exit_intent: "orange",
      gamified: "magenta",
    };
    return colors[type] || "default";
  };

  // Get status tag
  const getStatusTag = (promotion) => {
    const now = new Date();
    const startDate = promotion.schedule?.startDate
      ? new Date(promotion.schedule.startDate)
      : null;
    const endDate = promotion.schedule?.endDate
      ? new Date(promotion.schedule.endDate)
      : null;

    if (!promotion.isActive) {
      return <Tag color="default">Inactive</Tag>;
    }

    if (startDate && now < startDate) {
      return <Tag color="warning">Scheduled</Tag>;
    }

    if (endDate && now > endDate) {
      return <Tag color="error">Expired</Tag>;
    }

    return <Tag color="success">Active</Tag>;
  };

  // Calculate performance score
  const getPerformanceScore = (promotion) => {
    const views = promotion.analytics?.totalViews || 0;
    const clicks = promotion.analytics?.totalClicks || 0;
    const conversions = promotion.analytics?.totalConversions || 0;

    if (views === 0) return 0;

    const ctr = (clicks / views) * 100;
    const convRate = clicks > 0 ? (conversions / clicks) * 100 : 0;

    // Weighted score
    return Math.min((ctr * 0.4 + convRate * 0.6) * 10, 100);
  };

  // Table columns
  const columns = [
    ...(showRank
      ? [
          {
            title: "#",
            key: "rank",
            width: 50,
            render: (_, __, index) => {
              const rank = index + 1;
              if (rank <= 3) {
                const colors = ["#faad14", "#8c8c8c", "#cd7f32"];
                return (
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: colors[rank - 1],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    {rank}
                  </div>
                );
              }
              return (
                <Text type="secondary" style={{ paddingLeft: 8 }}>
                  {rank}
                </Text>
              );
            },
          },
        ]
      : []),
    {
      title: "Promotion",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color={getTypeColor(record.type)} style={{ fontSize: 10 }}>
              {record.type?.replace("_", " ").toUpperCase()}
            </Tag>
            {record.gamification?.enabled && (
              <Tag color="magenta" style={{ fontSize: 10 }}>
                <TrophyOutlined /> GAMIFIED
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "Views",
      key: "views",
      width: 100,
      sorter: (a, b) =>
        (a.analytics?.totalViews || 0) - (b.analytics?.totalViews || 0),
      render: (_, record) => (
        <Tooltip title="Total views">
          <Space>
            <EyeOutlined style={{ color: "#1890ff" }} />
            <Text>{(record.analytics?.totalViews || 0).toLocaleString()}</Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "Clicks",
      key: "clicks",
      width: 100,
      sorter: (a, b) =>
        (a.analytics?.totalClicks || 0) - (b.analytics?.totalClicks || 0),
      render: (_, record) => (
        <Tooltip title="Total clicks">
          <Space>
            <SelectOutlined style={{ color: "#52c41a" }} />
            <Text>{(record.analytics?.totalClicks || 0).toLocaleString()}</Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "CTR",
      key: "ctr",
      width: 100,
      sorter: (a, b) => {
        const ctrA =
          a.analytics?.totalViews > 0
            ? (a.analytics?.totalClicks || 0) / a.analytics.totalViews
            : 0;
        const ctrB =
          b.analytics?.totalViews > 0
            ? (b.analytics?.totalClicks || 0) / b.analytics.totalViews
            : 0;
        return ctrA - ctrB;
      },
      render: (_, record) => {
        const ctr =
          record.analytics?.totalViews > 0
            ? ((record.analytics?.totalClicks || 0) /
                record.analytics.totalViews) *
              100
            : 0;
        return (
          <Tag color={ctr > 5 ? "green" : ctr > 2 ? "orange" : "default"}>
            {ctr.toFixed(2)}%
          </Tag>
        );
      },
    },
    {
      title: "Conversions",
      key: "conversions",
      width: 110,
      sorter: (a, b) =>
        (a.analytics?.totalConversions || 0) -
        (b.analytics?.totalConversions || 0),
      render: (_, record) => (
        <Tooltip title="Total conversions">
          <Space>
            <DollarOutlined style={{ color: "#722ed1" }} />
            <Text strong>
              {(record.analytics?.totalConversions || 0).toLocaleString()}
            </Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "Revenue",
      key: "revenue",
      width: 120,
      sorter: (a, b) =>
        (a.analytics?.revenueGenerated || 0) -
        (b.analytics?.revenueGenerated || 0),
      render: (_, record) => (
        <Text strong style={{ color: "#52c41a" }}>
          ${(record.analytics?.revenueGenerated || 0).toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Performance",
      key: "performance",
      width: 140,
      render: (_, record) => {
        const score = getPerformanceScore(record);
        return (
          <Tooltip title={`Performance Score: ${score.toFixed(1)}`}>
            <Progress
              percent={score}
              size="small"
              strokeColor={
                score > 70
                  ? "#52c41a"
                  : score > 40
                  ? "#faad14"
                  : "#ff4d4f"
              }
              format={(percent) => (
                <span style={{ fontSize: 11 }}>
                  {percent > 70 ? (
                    <FireOutlined style={{ color: "#52c41a" }} />
                  ) : null}
                </span>
              )}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          {onViewAnalytics && (
            <Tooltip title="View Analytics">
              <Button
                type="text"
                size="small"
                icon={<BarChartOutlined />}
                onClick={() => onViewAnalytics(record._id)}
              />
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(record._id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (!data || data.length === 0) {
    return (
      <Card title={title} bordered={false} loading={loading} style={style}>
        <Empty
          description="No promotion data available"
          style={{ padding: "40px 0" }}
        />
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        title={
          <span>
            <TrophyOutlined style={{ marginRight: 8, color: "#faad14" }} />
            {title}
          </span>
        }
        bordered={false}
        loading={loading}
        style={style}
      >
        <Table
          columns={columns}
          dataSource={data.slice(0, limit)}
          pagination={false}
          rowKey={(record) => record._id}
          size="middle"
          scroll={{ x: "max-content" }}
        />
      </Card>
    </motion.div>
  );
};

export default TopPromotionsTable;
